import { Building2, ArrowRight, BarChart3, Users, Store, Target, Clock, IndianRupee, Receipt, RefreshCw, GraduationCap } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { WalkthroughButton } from './InteractiveWalkthrough';

export default function SuperAdminLanding({ brands, onSelectBrand }) {
  // Sort brands by customer count
  const sortedBrands = [...(brands || [])].sort((a, b) => {
    const aCustomers = a.data_stats?.total_customers || 0;
    const bCustomers = b.data_stats?.total_customers || 0;
    return bCustomers - aCustomers;
  });

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    if (num >= 10000000) return `${(num / 10000000).toFixed(2)}Cr`;
    if (num >= 100000) return `${(num / 100000).toFixed(2)}L`;
    if (num >= 1000) return `${(num / 1000).toFixed(2)}K`;
    return num.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  };

  const formatCurrency = (num) => {
    if (num === null || num === undefined) return '₹0';
    if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)}Cr`;
    if (num >= 100000) return `₹${(num / 100000).toFixed(2)}L`;
    if (num >= 1000) return `₹${(num / 1000).toFixed(2)}K`;
    return `₹${num.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  };

  const formatSyncTime = (syncTime) => {
    if (!syncTime) return 'Never';
    const date = new Date(syncTime);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    
    return date.toLocaleDateString('en-IN', { 
      day: 'numeric', 
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Kolkata'
    }) + ' IST';
  };

  // Calculate totals
  const totals = {
    brands: brands?.length || 0,
    customers: brands?.reduce((sum, b) => sum + (b.data_stats?.total_customers || 0), 0) || 0,
    stores: brands?.reduce((sum, b) => sum + (b.data_stats?.total_stores || 0), 0) || 0,
    transactions: brands?.reduce((sum, b) => sum + (b.data_stats?.total_transactions || 0), 0) || 0,
    revenue: brands?.reduce((sum, b) => sum + (b.data_stats?.total_revenue || 0), 0) || 0,
  };

  return (
    <div className="min-h-full p-6 space-y-6" data-testid="super-admin-landing">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Building2 className="w-7 h-7 text-purple-400" />
            Brand Overview
          </h1>
          <p className="text-gray-400 mt-1">Complete snapshot of all brands</p>
        </div>
        <WalkthroughButton 
          dashboardId="command-center" 
          className="bg-violet-600 hover:bg-violet-700 text-white border-0" 
        />
      </div>

      {/* Overall Stats */}
      <div className="grid gap-3 md:grid-cols-5">
        <Card className="bg-purple-900/20 border-purple-800/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Building2 className="w-6 h-6 text-purple-400" />
              <div>
                <p className="text-xl font-bold text-white">{totals.brands}</p>
                <p className="text-xs text-gray-400">Total Brands</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-blue-900/20 border-blue-800/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Users className="w-6 h-6 text-blue-400" />
              <div>
                <p className="text-xl font-bold text-white">{formatNumber(totals.customers)}</p>
                <p className="text-xs text-gray-400">Total Members</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-green-900/20 border-green-800/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Store className="w-6 h-6 text-green-400" />
              <div>
                <p className="text-xl font-bold text-white">{formatNumber(totals.stores)}</p>
                <p className="text-xs text-gray-400">Total Stores</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-orange-900/20 border-orange-800/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Receipt className="w-6 h-6 text-orange-400" />
              <div>
                <p className="text-xl font-bold text-white">{formatNumber(totals.transactions)}</p>
                <p className="text-xs text-gray-400">Total Bills</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-emerald-900/20 border-emerald-800/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <IndianRupee className="w-6 h-6 text-emerald-400" />
              <div>
                <p className="text-xl font-bold text-white">{formatCurrency(totals.revenue)}</p>
                <p className="text-xs text-gray-400">Total Revenue</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Brand Cards - Full Details */}
      <div className="space-y-3">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          <Target className="w-5 h-5 text-purple-400" />
          All Brands Snapshot
        </h2>
        
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {sortedBrands.map((brand) => {
            const stats = brand.data_stats || {};
            const repeatRatio = stats.repeat_ratio || 0;
            
            return (
              <Card 
                key={brand.brand_id}
                className="bg-gray-800/50 border-gray-700 hover:border-purple-600 cursor-pointer transition-all duration-200"
                onClick={() => onSelectBrand(brand)}
                data-testid={`brand-card-${brand.brand_id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg text-white">{brand.name}</CardTitle>
                    <Badge variant="outline" className="text-xs">
                      ID: {brand.brand_id}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <Clock className="w-3 h-3" />
                    <span>Last Sync: {formatSyncTime(stats.last_sync)}</span>
                  </div>
                </CardHeader>
                
                <CardContent className="pt-2">
                  {/* Main Metrics Grid */}
                  <div className="grid grid-cols-4 gap-2 text-center mb-3">
                    <div className="bg-gray-900/50 rounded p-2">
                      <p className="text-white font-semibold text-sm">{stats.total_stores || 0}</p>
                      <p className="text-gray-500 text-xs">Stores</p>
                    </div>
                    <div className="bg-gray-900/50 rounded p-2">
                      <p className="text-white font-semibold text-sm">{formatNumber(stats.total_customers)}</p>
                      <p className="text-gray-500 text-xs">Members</p>
                    </div>
                    <div className="bg-gray-900/50 rounded p-2">
                      <p className="text-white font-semibold text-sm">{formatNumber(stats.total_transactions)}</p>
                      <p className="text-gray-500 text-xs">Bills</p>
                    </div>
                    <div className="bg-gray-900/50 rounded p-2">
                      <p className="text-white font-semibold text-sm">{formatCurrency(stats.total_revenue)}</p>
                      <p className="text-gray-500 text-xs">Revenue</p>
                    </div>
                  </div>

                  {/* Cohort & ATV Row */}
                  <div className="grid grid-cols-3 gap-2 text-center mb-3">
                    <div className="bg-red-900/20 border border-red-800/30 rounded p-2">
                      <p className="text-red-400 font-semibold text-sm">{formatNumber(stats.one_timers)}</p>
                      <p className="text-gray-500 text-xs">One-Timers</p>
                    </div>
                    <div className="bg-green-900/20 border border-green-800/30 rounded p-2">
                      <p className="text-green-400 font-semibold text-sm">{formatNumber(stats.repeat_count)}</p>
                      <p className="text-gray-500 text-xs">Repeat</p>
                    </div>
                    <div className="bg-blue-900/20 border border-blue-800/30 rounded p-2">
                      <p className="text-blue-400 font-semibold text-sm">₹{Math.round(stats.avg_atv || 0).toLocaleString('en-IN')}</p>
                      <p className="text-gray-500 text-xs">ATV</p>
                    </div>
                  </div>

                  {/* Repeat Ratio Bar */}
                  <div className="mb-3">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-gray-400">Repeat Ratio</span>
                      <span className="text-white font-medium">{repeatRatio.toFixed(1)}%</span>
                    </div>
                    <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-purple-600 to-purple-400 rounded-full transition-all"
                        style={{ width: `${Math.min(repeatRatio, 100)}%` }}
                      />
                    </div>
                  </div>

                  <Button 
                    className="w-full bg-purple-600/20 hover:bg-purple-600 text-purple-300 hover:text-white border border-purple-600/50"
                    variant="outline"
                    size="sm"
                  >
                    View Dashboard
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}
